import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AppVersion } from '@ionic-native/app-version/ngx';
import { Device } from '@ionic-native/device/ngx';
import { TerminalDataService } from '../private/terminal/services/terminal-data.service';
import { TerminalStoreService } from '../private/terminal/services/terminal-store.service';
import { DesktopService } from '../services/desktop.service';
import { DeviceLogService, GetLogsPayload } from '../services/device-log.service';
import { ConnectionStatus, NetworkService } from '../services/network.service';
import { TenantAuthService } from '../services/tenant-auth.service';
import { TerminalAuthService } from '../services/terminal-auth.service';
import { CompanyStorage } from './company-storage';
import { DeviceWsConnector } from './device-ws.connector';
import { PosTerminalChatService } from './pos-terminal-chat.service';
import { PosTerminalCommunicationService } from './pos-terminal-communication.service';
import { PosTerminalSupportService } from './pos-terminal-support.service';
import {
  PosTerminalScreenshotService,
  ScreenshotCommandPayload,
} from './pos-terminal-screenshot.service';
import {
  DeleteStoragePayload,
  GetStoragePayload,
  PosTerminalStorageEditService,
  ReadStoragePayload,
  SetStoragePayload,
} from './pos-terminal-storage-edit.service';
import { PosTerminalStorageExportService } from './pos-terminal-storage-export.service';

export interface NavigateCommandPayload {
  // An app route, e.g. "/terminal" or "/lock-screen?x=1". External URLs are refused.
  route: string;
  replaceUrl?: boolean;
}

export interface ReloadCommandPayload {
  // Like the menu's reload: Cache Storage, service workers and, in the desktop app, the browser cache.
  resetCache?: boolean;
  // How long after answering the page reloads, so the result reaches the admin first.
  delayMs?: number;
}

const RELOAD_DELAY_MS = 1_000;
// Shorter and the page could reload before the answer has left the socket.
const RELOAD_DELAY_MIN_MS = 300;
const RELOAD_DELAY_MAX_MS = 10_000;

// App-level admin commands: getState (what the terminal is doing), navigate (open an app page),
// reload (reload the page, optionally resetting the cache), screenshot (capture the screen),
// uploadStorage (the whole company storage as JSON), getStorage/setStorage/deleteStorage (one
// storage key) and readStorage (the keys matching a pattern, read now); files go to the device's
// blob container. getLogs: the device log (DeviceLogService), newest first or after a cursor.
// Created at app start (AppComponent), like PosTerminalCommunicationService.
@Injectable({ providedIn: 'root' })
export class PosTerminalAppCommandsService {
  private readonly _startedAt = Date.now();

  constructor(
    communication: PosTerminalCommunicationService,
    private _router: Router,
    private _zone: NgZone,
    private _device: Device,
    private _appVersion: AppVersion,
    @Inject('ENVIRONMENT') private _environment: { version?: string },
    private _tenantAuth: TenantAuthService,
    private _terminalAuth: TerminalAuthService,
    private _terminalData: TerminalDataService,
    private _terminalStore: TerminalStoreService,
    private _network: NetworkService,
    private _deviceWsConnector: DeviceWsConnector,
    private _screenshot: PosTerminalScreenshotService,
    private _storageExport: PosTerminalStorageExportService,
    private _storageEdit: PosTerminalStorageEditService,
    private _companyStorage: CompanyStorage,
    private _support: PosTerminalSupportService,
    private _chat: PosTerminalChatService,
    private _desktop: DesktopService,
    private _deviceLog: DeviceLogService,
  ) {
    communication.register('getState', () => this.getState());
    communication.register('navigate', (payload: NavigateCommandPayload) =>
      this.navigate(payload),
    );
    communication.register('reload', (payload: ReloadCommandPayload) => this.reload(payload));
    communication.register('screenshot', (payload: ScreenshotCommandPayload, envelope) =>
      this._screenshot.capture(payload, envelope.correlationId),
    );
    communication.register('uploadStorage', (_payload, envelope) =>
      this._storageExport.upload(envelope.correlationId),
    );
    communication.register('getStorage', (payload: GetStoragePayload) => this._storageEdit.get(payload));
    communication.register('setStorage', (payload: SetStoragePayload) => this._storageEdit.set(payload));
    communication.register('deleteStorage', (payload: DeleteStoragePayload) => this._storageEdit.delete(payload));
    communication.register('readStorage', (payload: ReadStoragePayload) => this._storageEdit.read(payload));
    communication.register('getOverview', () => this.getOverview());
    communication.register('getLogs', (payload: GetLogsPayload) => this._deviceLog.getLogs(payload));
  }

  // For the admin's overview: getState plus the organization and store, how much reference data
  // the terminal holds, its company storage keys and the support session.
  async getOverview() {
    const state = await this.getState();
    const settings = this._safe(() => this._terminalData.terminalSettings());
    const tenant = this._safe(() => this._tenantAuth.currentTenant);
    const count = (value: unknown) => (Array.isArray(value) ? value.length : 0);

    return {
      ...state,
      company: {
        tenantId: tenant?.clientid || null,
        tenantName: tenant?.name || null,
        ...this._fields(settings?.company),
      },
      store: this._fields(settings?.store),
      terminalSettings: this._fields(settings?.terminal),
      data: {
        employees: count(settings?.employees),
        customers: count(settings?.customers),
        discounts: count(settings?.discounts),
        paymentMethods: count(settings?.payment_methods),
        halls: count(settings?.halls),
        partners: count(settings?.partners),
        loyaltyPrograms: count(settings?.loyaltyprograms),
        suppliers: count(settings?.suppliers),
        supplyProducts: count(settings?.supply_products),
        receiptWishes: count(settings?.receipt_wishes),
        cashboxShiftOpened: !!settings?.cashbox_shift?.id,
      },
      storage: tenant?.clientid ? await this._storageSummary().catch((error) => ({ error: String(error?.message || error) })) : null,
      support: {
        session: this._safe(() => this._support.state.status) || null,
        operator: this._safe(() => this._support.state.operator) || null,
        chat: this._safe(() => this._chat.state.status) || null,
        chatMessages: this._safe(() => this._chat.state.messages.length) || 0,
      },
    };
  }

  // Keys with their type and item count, the largest lists first (no values: they can be MBs).
  private async _storageSummary() {
    const keys: string[] = ((await this._companyStorage.keys()) || []).slice().sort();
    const items = [];
    for (const key of keys) {
      const value = await this._companyStorage.get(key);
      items.push({
        key,
        type: value === null || value === undefined ? 'null' : Array.isArray(value) ? 'array' : typeof value,
        items: Array.isArray(value) ? value.length : value && typeof value === 'object' ? Object.keys(value).length : null,
      });
    }

    return {
      keys: keys.length,
      largest: items
        .filter((i) => i.items != null)
        .sort((a, b) => b.items - a.items)
        .slice(0, 12),
    };
  }

  // The plain fields of a settings object (nested objects and lists left out, long texts cut).
  private _fields(source: unknown): Record<string, unknown> {
    const fields: Record<string, unknown> = {};
    if (!source || typeof source !== 'object') {
      return fields;
    }

    for (const [name, value] of Object.entries(source as Record<string, unknown>)) {
      if (value === null || ['string', 'number', 'boolean'].includes(typeof value)) {
        fields[name] = typeof value === 'string' && value.length > 300 ? `${value.slice(0, 300)}…` : value;
      }
    }
    return fields;
  }

  async getState() {
    const settings = this._safe(() => this._terminalData.terminalSettings());
    const store = settings?.store;
    const employee = this._safe(() => this._terminalAuth.currentUser);
    const now = new Date();

    return {
      device: {
        id: this._device.uuid,
        platform: this._device.platform,
        model: this._device.model,
        isVirtual: this._safe(() => this._device.isVirtual),
      },
      app: {
        version: await this._getAppVersion(),
        url: this._router.url,
        uptimeSeconds: Math.round((Date.now() - this._startedAt) / 1000),
        language: navigator.language,
      },
      connection: {
        network:
          this._safe(() => this._network.getCurrentNetworkStatus()) === ConnectionStatus.Offline
            ? 'offline'
            : 'online',
        browserOnline: navigator.onLine,
        webSocket: this._deviceWsConnector.connectionState,
      },
      tenantId: this._safe(() => this._tenantAuth.tenantId) || null,
      store: store
        ? {
            id: store.id ?? null,
            name: store.name || store.erpName || null,
            erpStoreId: store.erpStoreId ?? null,
            selectedStoreId: this._safe(() => this._terminalStore.getTerminalStoreId()) ?? null,
          }
        : null,
      employee: employee
        ? {
            id: employee.id ?? null,
            userId: employee.userid ?? null,
            name:
              employee.full_name ||
              [employee.firstname, employee.lastname].filter(Boolean).join(' ') ||
              null,
          }
        : null,
      time: {
        utc: now.toISOString(),
        timeZone: this._safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone) || null,
        utcOffsetMinutes: -now.getTimezoneOffset(),
      },
      screen: {
        width: window.innerWidth,
        height: window.innerHeight,
        orientation: this._safe(() => screen.orientation?.type) || null,
      },
    };
  }

  async navigate(payload: NavigateCommandPayload) {
    const route = typeof payload?.route === 'string' ? payload.route.trim() : '';
    // Only paths inside the app: "/x", not "//host", "http:..." or "javascript:...".
    if (!route.startsWith('/') || route.startsWith('//')) {
      throw new Error(`Route must be an app path starting with "/", got "${route}"`);
    }

    const previousUrl = this._router.url;
    const navigated = await this._zone.run(() =>
      this._router.navigateByUrl(route, { replaceUrl: !!payload.replaceUrl }),
    );

    // false: a guard refused it, or it is the current page.
    return { navigated, url: this._router.url, previousUrl };
  }

  // Answers first and reloads the page after delayMs; the terminal reconnects after the reload.
  async reload(payload?: ReloadCommandPayload) {
    const resetCache = !!payload?.resetCache;
    const cache = resetCache ? await this._resetCaches() : null;
    const delayMs = Math.min(
      Math.max(Number(payload?.delayMs) || RELOAD_DELAY_MS, RELOAD_DELAY_MIN_MS),
      RELOAD_DELAY_MAX_MS,
    );

    setTimeout(() => this._reloadPage(), delayMs);
    return { reloadingInMs: delayMs, url: this._router.url, resetCache, cache };
  }

  protected _reloadPage() {
    window.location.reload();
  }

  // Best effort: each part that fails is reported, the reload still happens.
  private async _resetCaches() {
    const result = {
      cacheStorage: 0,
      serviceWorkers: 0,
      desktopBrowserCache: false,
      errors: [] as string[],
    };

    try {
      if (typeof caches !== 'undefined') {
        for (const name of await caches.keys()) {
          if (await caches.delete(name)) {
            result.cacheStorage++;
          }
        }
      }
    } catch (err) {
      result.errors.push(`Cache Storage: ${err?.message || err}`);
    }

    try {
      const registrations = (await navigator.serviceWorker?.getRegistrations?.()) || [];
      for (const registration of registrations) {
        if (await registration.unregister()) {
          result.serviceWorkers++;
        }
      }
    } catch (err) {
      result.errors.push(`Service workers: ${err?.message || err}`);
    }

    // The desktop host clears the WebView2 browser cache, as the menu's reload does.
    if (this._desktop.isDesktop) {
      try {
        this._desktop.resetCache();
        result.desktopBrowserCache = true;
      } catch (err) {
        result.errors.push(`Desktop browser cache: ${err?.message || err}`);
      }
    }

    return result;
  }

  private async _getAppVersion(): Promise<string | null> {
    if (this._device.platform !== 'web_browser') {
      try {
        return await this._appVersion.getVersionNumber();
      } catch {
        // falls back to the build version
      }
    }

    return this._environment?.version || null;
  }

  // State is best effort: one missing source must not fail the whole command.
  private _safe<T>(read: () => T): T | undefined {
    try {
      return read();
    } catch {
      return undefined;
    }
  }
}
